import { useQuery } from 'react-query'
import type { FetchProjectsResult } from '../services'
import { fetchLibraryProjects, fetchMyProjects } from '../services'
import type {
  ArcProject,
  DisplayStatus,
  OrderByOption,
  PaginationMeta,
  ProjectsQueryParams,
  ProjectTab,
  SortDirection,
} from '../types'
import { DISPLAY_TO_API_STATUS } from '../types'
import type { GradeRange } from '../types'

interface UseArcProjectsParams {
  tab: ProjectTab
  status: DisplayStatus
  search: string
  orderBy: OrderByOption
  direction: SortDirection
  page: number
  limit?: number
  gradeId?: string
  gradeRange?: GradeRange
  categoryId?: string
}

interface UseArcProjectsReturn {
  projects: ArcProject[]
  pagination: PaginationMeta | null
  isLoading: boolean
  isFetching: boolean
  isError: boolean
  error: Error | null
  refetch: () => void
}

const STALE_TIME_MY = 5 * 60 * 1000
const STALE_TIME_LIBRARY = 30 * 60 * 1000
const DEFAULT_LIMIT = 9

const buildApiParams = (
  params: UseArcProjectsParams,
): ProjectsQueryParams => {
  const apiParams: ProjectsQueryParams = {
    page: params.page,
    limit: params.limit || DEFAULT_LIMIT,
  }

  if (params.search) {
    apiParams.name = params.search
  }

  if (params.tab === 'my') {
    const apiStatus = DISPLAY_TO_API_STATUS[params.status]
    if (apiStatus) {
      apiParams.status = apiStatus
    }
  }

  if (params.orderBy !== 'created_at' || params.direction !== 'desc') {
    apiParams.order_by = params.orderBy
    apiParams.direction = params.direction
  }

  if (params.gradeRange && params.gradeRange !== 'all') {
    apiParams.grade_range = params.gradeRange
  }

  if (params.categoryId) {
    apiParams.category_id = params.categoryId
  }

  if (params.gradeId) {
    apiParams.grade_id = params.gradeId
  }

  return apiParams
}

export const useArcProjects = (params: UseArcProjectsParams): UseArcProjectsReturn => {
  const { tab, status, search, orderBy, direction, page, limit, gradeId, gradeRange, categoryId } = params

  const apiParams = buildApiParams(params)

  const queryKey = ['arc-projects', tab, status, search, orderBy, direction, page, limit, gradeId, gradeRange, categoryId]

  const queryFn = tab === 'my'
    ? () => fetchMyProjects(apiParams)
    : () => fetchLibraryProjects(apiParams)

  const staleTime = tab === 'my' ? STALE_TIME_MY : STALE_TIME_LIBRARY

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery<FetchProjectsResult, Error>(queryKey, queryFn, {
    staleTime,
    keepPreviousData: true,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 2,
  })

  return {
    projects: data?.projects ?? [],
    pagination: data?.pagination ?? null,
    isLoading,
    isFetching,
    isError,
    error: error ?? null,
    refetch,
  }
}
